import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './ui.css';

/**
 * Ayuda contextual ⓘ: se muestra al pasar el mouse (escritorio) y al tocar el ícono (teléfono).
 * `amplio`: popover informativo (texto largo). Se dibuja en una capa propia (portal, posición fija) para que
 * ningún contenedor con overflow lo corte; se ajusta al ancho de la pantalla con margen y nunca se sale de ella.
 */
export default function InfoTip({ texto, etiqueta = 'Más información', amplio = false }) {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const pop = useRef(null);
  const id = useId();
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (!ref.current?.contains(e.target) && !pop.current?.contains(e.target)) setAbierto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    const cerrar = () => setAbierto(false);
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', esc);
    if (amplio) { window.addEventListener('resize', cerrar); window.addEventListener('scroll', cerrar, true); }
    return () => {
      document.removeEventListener('pointerdown', fuera); document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', cerrar); window.removeEventListener('scroll', cerrar, true);
    };
  }, [abierto, amplio]);
  // Popover amplio: debajo del ícono (o arriba si no cabe), con 12 px de margen a los lados de la pantalla.
  useLayoutEffect(() => {
    if (!amplio || !abierto || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, margen = 12;
    const ancho = Math.min(360, vw - margen * 2);
    const left = Math.min(Math.max(margen, r.left + r.width / 2 - ancho / 2), vw - margen - ancho);
    const alto = pop.current?.offsetHeight || 0;
    const abajo = r.bottom + 8 + alto <= vh - margen || r.top - 8 - alto < margen;
    setPos({ left, width: ancho, top: abajo ? r.bottom + 8 : r.top - 8 - alto });
  }, [abierto, amplio]);

  const boton = (
    <button type="button" className={`fx-infotip-btn${amplio ? ' fx-infotip-btn--grande' : ''}`} aria-label={etiqueta} aria-describedby={id} aria-expanded={abierto}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAbierto((v) => !v); }}>ⓘ</button>
  );
  if (amplio) {
    return (
      <span className="fx-infotip" ref={ref}>
        {boton}
        {abierto && createPortal(
          <div ref={pop} role="tooltip" id={id} className="fx-popover" style={pos ? { left: pos.left, top: pos.top, width: pos.width } : { visibility: 'hidden' }}>{texto}</div>,
          document.body)}
      </span>
    );
  }
  return (
    <span className={`fx-infotip${abierto ? ' fx-infotip--abierto' : ''}`} ref={ref}>
      {boton}
      <span role="tooltip" id={id} className="fx-infotip-texto">{texto}</span>
    </span>
  );
}
